/**
 * Reorder reminder opt-in.
 *
 * A "subscribe and save"-style selector that does NOT create a recurring
 * Shopify subscription. When the reminders option is chosen, it injects a
 * hidden line-item property (e.g. `properties[Reorder reminders]=Enrolled`)
 * into the product's add-to-cart form so the resulting one-time order is
 * tagged for enrollment. Horizon serializes the whole form (new FormData(form)
 * in product-form.js), so the property is submitted for both the standard
 * add-to-cart and the accelerated (Shop Pay etc.) checkout.
 *
 * No shared section liquid is modified: this only reads/writes DOM.
 */
class ReorderReminderOptin extends HTMLElement {
  connectedCallback() {
    this.propName = this.dataset.propertyName || 'Reorder reminders';
    this.propValue = this.dataset.propertyValue || 'Enrolled';
    // Scope to the product section so we don't touch unrelated forms.
    this.root = this.closest('.shopify-section') || document;

    const selected = this.querySelector('input[type="radio"]:checked');
    this.enrolled = !!selected && selected.value === 'subscribe';

    this.onChange = (event) => {
      const radio = event.target.closest && event.target.closest('input[type="radio"]');
      if (!radio || !this.contains(radio)) return;
      this.enrolled = radio.value === 'subscribe';
      this.sync();
    };
    this.addEventListener('change', this.onChange);

    // The buy box re-renders on variant change; re-apply after any such morph.
    this.observer = new MutationObserver(() => this.sync());

    // Insurance: guarantee the property is present the instant an add-to-cart
    // form submits, before product-form.js reads FormData (capture phase).
    this.onSubmit = () => this.apply();
    this.root.addEventListener('submit', this.onSubmit, true);

    this.sync();
  }

  disconnectedCallback() {
    this.observer && this.observer.disconnect();
    this.removeEventListener('change', this.onChange);
    this.root && this.root.removeEventListener('submit', this.onSubmit, true);
  }

  forms() {
    return this.root.querySelectorAll('form[data-type="add-to-cart-form"]');
  }

  /** Add or remove the hidden property input across all product forms. */
  apply() {
    const name = 'properties[' + this.propName + ']';
    this.forms().forEach((form) => {
      let input = form.querySelector('input[data-reorder-reminder]');
      if (this.enrolled) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.setAttribute('data-reorder-reminder', '');
          form.appendChild(input);
        }
        input.name = name;
        input.value = this.propValue;
      } else if (input) {
        input.remove();
      }
    });
  }

  /** apply(), guarding the observer against its own mutations. */
  sync() {
    if (this.observer) this.observer.disconnect();
    this.apply();
    if (this.observer) this.observer.observe(this.root, { childList: true, subtree: true });
  }
}

if (!customElements.get('reorder-reminder-optin')) {
  customElements.define('reorder-reminder-optin', ReorderReminderOptin);
}
